import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  CellMouseDownEvent,
  ColDef,
  GridOptions,
  RowSelectionOptions,
} from 'ag-grid-community';
import { Action, Column } from '../store/view/types';
import { BadgeListComponent } from '../components/badge/badge-list/badge-list.component';
import { BadgeComponent } from '../components/badge/badge/badge.component';
import { BadgeModel } from '../models/badge-model';
import { PhoneNumberComponent } from '../components/phone-number/phone-number.component';
import { FileSizePipe } from '../pipe/file-size.pipe';
import { ActionLinkComponent } from '../components/generic-grid/action-link/action-link.component';
import { ColoredAmountComponent } from '../components/colored-amount/colored-amount.component';
import { TimeZoneService } from 'src/app/common/time-zone/time-zone.service';
import { HRefGridComponent } from '../components/generic-grid/href-grid/href-grid-component';
import { DocumentLinkComponent } from '../components/generic-grid/document-link/document-link.component';
import { AccountMoveIssuingDetailsComponent } from '../components/generic-grid/account-move-issuing-details/account-move-issuing-details.component';
import { IconComponent } from '../components/generic-grid/icon/icon.component';
import { OffcanvasTriggerComponent } from '../components/generic-grid/offcanvas-trigger/offcanvas-trigger.component';
import { SuccessComponent } from '../components/generic-grid/success/success.component';
import { SmartActionLinkComponent } from '../components/generic-grid/smart-action-link/smart-action-link.component';

@Injectable({
  providedIn: 'root',
})
export class GridService {
  /** Colonnes techniques qui ne doivent jamais déclencher la navigation de ligne. */
  private readonly SELF_HANDLED_COL_IDS = [
    'ag-Grid-SelectionColumn', //checkbox de sélection ag-grid
    'actions',
    'offcanvasTrigger',
  ];

  /** Renderers qui portent leur propre action au clic. */
  private readonly SELF_HANDLED_RENDERERS: unknown[] = [
    HRefGridComponent,
    DocumentLinkComponent,
    SmartActionLinkComponent,
    OffcanvasTriggerComponent,
    ActionLinkComponent,
  ];

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly timeZoneService: TimeZoneService,
  ) {}

  getConfig(): GridOptions<any> {
    let gridOptions: GridOptions = {};

    gridOptions.getRowStyle = (params) => {
      if (params.node.rowPinned && params.data?.['isBoldRowStyle']) {
        return { fontWeight: 'bold' };
      }
      return undefined;
    };

    gridOptions.suppressCellFocus = true;
    gridOptions.headerHeight = 37.2;
    gridOptions.animateRows = true;
    gridOptions.suppressMiddleClickScrolls = true;

    gridOptions.selectionColumnDef = {
      sortable: false,
      width: 60,
      maxWidth: 60,
      suppressHeaderMenuButton: false,
    };

    gridOptions.localeText = {
      // General
      filterOoo: 'Filtrer...',
      equals: 'Égal à',
      notEqual: 'Différent de',
      lessThan: 'Inférieur à',
      greaterThan: 'Supérieur à',
      lessThanOrEqual: 'Inférieur ou égal à',
      greaterThanOrEqual: 'Supérieur ou égal à',
      inRange: 'Dans la plage',
      contains: 'Contient',
      notContains: 'Ne contient pas',
      startsWith: 'Commence par',
      endsWith: 'Se termine par',

      // Set Filter
      selectAll: '(Tout sélectionner)',
      selectAllSearchResults: '(Tout sélectionner dans les résultats)',
      searchOoo: 'Rechercher...',
      blanks: '(Vides)',
      noMatches: 'Aucune correspondance',

      // Number Filter
      numberFilterOoo: 'Filtrer par nombre...',
      applyFilter: 'Appliquer',

      // Date Filter
      dateFormatOoo: 'jj/mm/aaaa',

      // Pagination
      page: 'Page',
      more: 'Plus',
      to: 'à',
      of: 'sur',
      next: 'Suivant',
      last: 'Dernier',
      first: 'Premier',
      previous: 'Précédent',
    };

    return gridOptions;
  }

  getRowSelectionConfig(view: string | undefined) {
    let rowSelectionOptions: RowSelectionOptions | undefined;

    if (view == undefined) return (rowSelectionOptions = undefined);

    rowSelectionOptions = {
      mode: 'multiRow',
      isRowSelectable: (rowNode) => true,
      headerCheckbox: true,
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
      hideDisabledCheckboxes: true,
    };

    return rowSelectionOptions;
  }

  /**
   * Vrai si la cellule gère elle-même le clic : la grille ne doit alors pas
   * naviguer vers le détail de la ligne.
   */
  isRowNavigationSuppressed(event: CellMouseDownEvent): boolean {
    const colId = event.column?.getColId();
    if (
      this.SELF_HANDLED_COL_IDS.includes(event.colDef?.colId ?? '') ||
      this.SELF_HANDLED_COL_IDS.includes(colId ?? '')
    )
      return true;

    return this.SELF_HANDLED_RENDERERS.includes(event.colDef?.cellRenderer);
  }

  renderCustomColumn(column: Column, colDef: ColDef): ColDef {
    if (!column || !colDef) return colDef;

    switch (column.type) {
      case 'utcDate':
      case 'utcDateOnly':
      case 'date':
      case 'dateOnly': {
        const render = (p) => this.formatValue(column.type, p.value);
        colDef.cellRenderer = render;
        colDef.tooltipValueGetter = render;
        return colDef;
      }
      case 'badges':
        colDef.cellRenderer = BadgeListComponent;
        return colDef;
      case 'badge':
        colDef.cellRenderer = BadgeComponent;
        colDef.cellRendererParams = <BadgeModel>{
          name: column.name,
          color: column.color,
        };
        return colDef;
      case 'checkbox':
        colDef.cellRenderer = (params) => {
          return `<input type='checkbox' ${
            params.value ? 'checked' : ''
          } disabled />`;
        };
        return colDef;
      case 'booleanRead':
        colDef.cellRenderer = (params) => {
          return params.value ? '✓' : '—';
        };
        return colDef;
      case 'activeBadge':
        colDef.cellRenderer = BadgeComponent;
        colDef.cellRendererParams = <BadgeModel>{
          name: column.name,
          color: column.color,
        };
        return colDef;
      case 'phoneNumber':
        colDef.cellRenderer = PhoneNumberComponent;
        return colDef;
      case 'number':
        colDef.cellClass = 'ag-right-aligned-cell';
        colDef.headerClass = 'ag-right-aligned-header';
        return colDef;
      case 'numberWith2Digits':
      case 'currency':
      case 'currencyWithSeparator':
      case 'percent':
        colDef.cellClass = 'ag-right-aligned-cell';
        colDef.headerClass = 'ag-right-aligned-header';
        colDef.cellRenderer = (p) => this.formatValue(column.type, p.value);
        return colDef;
      case 'fileSize':
      case 'fileSizeFull':
      case 'array':
        colDef.cellRenderer = (p) => this.formatValue(column.type, p.value);
        return colDef;
      case 'actions':
        colDef.cellRenderer = ActionLinkComponent;
        colDef.tooltipField = undefined;
        return colDef;
      case 'coloredAmount':
        colDef.cellClass = 'ag-right-aligned-cell';
        colDef.headerClass = 'ag-right-aligned-header';
        colDef.cellRenderer = ColoredAmountComponent;
        return colDef;
      case 'href':
        colDef.cellRenderer = HRefGridComponent;
        colDef.cellRendererParams = column.action;
        return colDef;
      case 'downloadDocument':
        colDef.cellRenderer = DocumentLinkComponent;
        return colDef;
      case 'showIssuingAccountMove':
        colDef.cellRenderer = AccountMoveIssuingDetailsComponent;
        return colDef;
      case 'icon':
        colDef.cellRenderer = IconComponent;
        colDef.cellRendererParams = { tooltip: column.tooltip };
        return colDef;
      case 'success-badge':
        colDef.cellRenderer = SuccessComponent;
        return colDef;
      case 'smart-action-link-filter':
        colDef.cellClass = 'ag-right-aligned-cell';
        colDef.headerClass = 'ag-right-aligned-header';

        colDef.cellRenderer = SmartActionLinkComponent;
        colDef.cellRendererParams = column.action;

        return colDef;
      case 'tooltip': {
        const render = (p) => this.formatValue('', p.value);
        colDef.cellRenderer = render;
        colDef.tooltipValueGetter = render;
        return colDef;
      }
      default: {
        const render = (p) => this.formatValue('', p.value);
        colDef.cellRenderer = render;
        return colDef;
      }
    }
  }

  getEmptyColumnDef(): ColDef<any> {
    const colDef: ColDef = {
      field: '',
      headerName: '',
      sortable: false,
      suppressMovable: true, //column not movable for all
      sort: null,
      sortingOrder: [null],
      resizable: false,
      unSortIcon: false, //Icon to show sort is possible
      suppressAutoSize: true,
      width: 20,
      maxWidth: 30,
    };

    return colDef;
  }

  getOffcanvasColumnDef(): ColDef<any> {
    const colDef: ColDef = {
      colId: 'offcanvasTrigger',
      field: '',
      headerName: '',
      sortable: false,
      suppressMovable: true, //column not movable for all
      sort: null,
      sortingOrder: [null],
      resizable: false,
      unSortIcon: false, //Icon to show sort is possible
      suppressSizeToFit: true,
      width: 50,
      maxWidth: 50,
      // pinned: 'right',
    };
    colDef.cellRenderer = OffcanvasTriggerComponent;
    colDef.cellClass = 'offcanvas-trigger-cell';

    return colDef;
  }

  formatValue(type: string, value: any): string {
    switch (type) {
      case 'utcDate':
        return value
          ? new Date(value).toLocaleString('fr-FR', {
              dateStyle: 'short',
              timeStyle: 'short',
            })
          : '-';
      case 'utcDateOnly':
        return value
          ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'short' })
          : '-';
      case 'date':
        return value
          ? this.timeZoneService.fromUtcToLocal(value, 'dd-MM-yyyy HH:mm')
          : '-';
      case 'dateOnly':
        return value
          ? this.timeZoneService.fromUtcToLocal(value, 'dd-MM-yyyy')
          : '-';
      case 'currency': {
        const numberValue = this.toNumber(value);
        return numberValue === null ? '-' : `${numberValue.toFixed(2)} €`;
      }
      case 'currencyWithSeparator': {
        const numberValue = this.toNumber(value);
        return numberValue === null
          ? '-'
          : `${numberValue.toLocaleString('fr-FR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} €`;
      }
      case 'percent': {
        const numberValue = this.toNumber(value);
        return numberValue === null ? '-' : `${numberValue.toFixed(2)} %`;
      }
      case 'numberWith2Digits': {
        const numberValue = this.toNumber(value);
        return numberValue === null ? '-' : numberValue.toFixed(2);
      }
      case 'fileSize':
        return value
          ? new FileSizePipe().transform(value, false)
          : value ?? '-';
      case 'fileSizeFull':
        return value ? new FileSizePipe().transform(value, true) : value ?? '-';
      case 'array':
        return Array.isArray(value) ? value.join(', ') : '-';
      default:
        return value ?? '-';
    }
  }

  //Number.isNaN ne coerce pas : null, undefined ou '' ne sont pas NaN, il faut donc les exclure avant conversion
  private toNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;

    const numberValue = Number(value);

    return Number.isNaN(numberValue) ? null : numberValue;
  }
}
